export type PlanType = 'starter' | 'pro';
export type BillingType = 'monthly' | 'annual';
export type AnalyticsTier = 'basic' | 'advanced';
export type SubscriptionStatus = 'active' | 'canceled' | 'past_due' | 'unpaid';

export interface Entitlements {
  id: string;
  user_id: string;
  plan: PlanType;
  billing: BillingType;
  event_room: boolean;
  max_participants: 3 | 8 | 50;
  analytics_tier: AnalyticsTier;
  analytics_retention_days: 14 | 365;
  calendar_integrations: boolean;
  priority_support: boolean;
  created_at: string;
  updated_at: string;
}

export interface BillingSubscription {
  id: string;
  user_id: string;
  stripe_subscription_id?: string;
  stripe_customer_id?: string;
  plan: PlanType;
  billing: BillingType;
  event_room_addon: boolean;
  status: SubscriptionStatus;
  current_period_start?: string;
  current_period_end?: string;
  created_at: string;
  updated_at: string;
}

export interface SessionParticipant {
  id: string;
  session_id: string;
  user_id?: string; // NULL for guests
  guest_name?: string; // For non-authenticated users
  joined_at: string;
  left_at?: string;
  is_host: boolean;
}

export interface PlanFeatures {
  plan: PlanType;
  max_participants: number;
  analytics_tier: AnalyticsTier;
  analytics_retention_days: number;
  calendar_integrations: boolean;
  priority_support: boolean;
  event_room_available: boolean;
  price_monthly: number;
  price_annual: number;
  features: string[];
}

export interface UpgradePrompt {
  type: 'capacity_reached' | 'feature_required' | 'plan_upgrade';
  title: string;
  description: string;
  current_plan: PlanType;
  required_plan: PlanType;
  action: 'upgrade' | 'add_event_room' | 'contact_support';
  cta_text: string;
  cta_url?: string;
}

// Plan feature definitions
export const PLAN_FEATURES: Record<PlanType, PlanFeatures> = {
  starter: {
    plan: 'starter',
    max_participants: 3,
    analytics_tier: 'advanced',
    analytics_retention_days: 365,
    calendar_integrations: false,
    priority_support: false,
    event_room_available: false,
    price_monthly: 0,
    price_annual: 0,
    features: [
      'Core focus sessions',
      'Advanced analytics',
      'Up to 3 participants per session',
      'Standard support'
    ]
  },
  pro: {
    plan: 'pro',
    max_participants: 8,
    analytics_tier: 'advanced',
    analytics_retention_days: 365,
    calendar_integrations: true,
    priority_support: true,
    event_room_available: true,
    price_monthly: 9,
    price_annual: 90,
    features: [
      'Google Calendar sync',
      'Up to 8 participants per session',
      'Priority support (24h response)',
      'Event Room add-on available'
    ]
  }
};

// Event Room add-on features
export const EVENT_ROOM_FEATURES = {
  price_monthly: 19,
  max_participants: 50,
  features: [
    'Host up to 50 participants in a single room',
    'Performance optimizations for large groups',
    'Audio-only mode for >25 participants',
    'Stage/broadcast mode options'
  ]
};

// Helper functions
export function getPlanFeatures(plan: PlanType, eventRoom: boolean = false): PlanFeatures {
  const baseFeatures = PLAN_FEATURES[plan];
  
  if (eventRoom && plan === 'pro') {
    return {
      ...baseFeatures,
      max_participants: 50,
      event_room_available: true
    };
  }
  
  return baseFeatures;
}

export function canJoinSession(
  currentParticipants: number,
  maxParticipants: number
): boolean {
  return currentParticipants < maxParticipants;
}

export function getUpgradePrompt(
  type: UpgradePrompt['type'],
  currentPlan: PlanType,
  context?: any
): UpgradePrompt {
  switch (type) {
    case 'capacity_reached':
      return {
        type: 'capacity_reached',
        title: 'Session at capacity',
        description: `This session fits ${context?.currentLimit || 3} people. Need more room?`,
        current_plan: currentPlan,
        required_plan: 'pro',
        action: currentPlan === 'starter' ? 'upgrade' : 'add_event_room',
        cta_text: currentPlan === 'starter' ? 'Upgrade to Pro' : 'Add Event Room',
        cta_url: currentPlan === 'starter' ? '/pricing' : '/billing'
      };
    
    case 'feature_required':
      return {
        type: 'feature_required',
        title: 'Feature requires Pro plan',
        description: context?.feature || 'This feature is available with Pro.',
        current_plan: currentPlan,
        required_plan: 'pro',
        action: 'upgrade',
        cta_text: 'Upgrade to Pro',
        cta_url: '/pricing'
      };
    
    case 'plan_upgrade':
      return {
        type: 'plan_upgrade',
        title: 'Unlock more features',
        description: 'Upgrade your plan to access advanced analytics and more participants.',
        current_plan: currentPlan,
        required_plan: 'pro',
        action: 'upgrade',
        cta_text: 'View Plans',
        cta_url: '/pricing'
      };
    
    default:
      throw new Error(`Unknown upgrade prompt type: ${type}`);
  }
}

export function formatPrice(amount: number, billing: BillingType): string {
  if (amount === 0) return 'Free';

  // Format without decimals for whole numbers
  const isWholeNumber = amount % 1 === 0;
  const formatted = new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: isWholeNumber ? 0 : 2,
    maximumFractionDigits: isWholeNumber ? 0 : 2
  }).format(amount);

  return billing === 'annual' ? `${formatted}/year` : `${formatted}/month`;
}

export function calculateAnnualSavings(monthlyPrice: number): number {
  // Calculate actual savings: (monthly * 12) - annual price
  // For €9/month: (€9 * 12) - €90 = €108 - €90 = €18 (2 months free)
  const annualPrice = 90; // Annual subscription price in EUR
  return Math.round((monthlyPrice * 12 - annualPrice) * 100) / 100;
}
